import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { PanelFrame } from './panel-frame'
import { friendlyTrail, useNavManifest } from '../use-nav-manifest'

/**
 * `context`: exactly what the agent can see, live.
 *
 * THE TRUST PANEL. It exists because the agent told somebody they were on
 * Booking Types when they were not, and there was no way to check. The
 * live route is read off the browser every turn and was always correct;
 * what was missing was any way for a person to see what it had been told.
 *
 * NAMES FIRST, IDS UNDERNEATH. `/03126055-361f-11ea-9472-.../venues/
 * FI2EOLUNB3EZ/bookings/day-planner-fast` is a true answer to "which page
 * am I on" and a useless one. The breadcrumb comes from the SAME manifest
 * the agent navigates by, so what this panel says a page is called is
 * what the agent would call it. The literal route and the raw ids stay,
 * quieter, because they are what you paste into a bug report.
 *
 * IT ALSO STATES WHAT IT CANNOT SEE. Same discipline as a report: the
 * gaps go in quieter, never hidden. An agent that appears omniscient is
 * one whose every wrong answer is a shock.
 */
export function ContextPanel({
  onClose,
  route,
  venueSerial,
  venueName,
  orgId,
  orgName,
  caseId,
  movedTo,
  voice,
  muted,
}: {
  onClose: () => void
  route: string
  venueSerial?: string
  /** The venue's alias, resolved from the serial. */
  venueName?: string
  orgId?: string
  orgName?: string
  caseId?: string
  movedTo?: string | null
  voice: string
  muted: boolean
}) {
  const { items } = useNavManifest()
  const trail = friendlyTrail(items, route, { org: orgName, venue: venueName })

  return (
    <PanelFrame id="context" onClose={onClose} note="read fresh every turn">
      <div className="flex min-w-0 flex-col divide-y divide-black/5 dark:divide-white/5">
        <Fact label="page">
          {trail.length > 0 ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
              {trail.map((step, i) => (
                <span key={`${step}-${String(i)}`} className="flex items-center gap-1">
                  {i > 0 ? (
                    <ChevronRight className="size-3 shrink-0 text-muted-foreground/40" />
                  ) : null}
                  <span
                    className={
                      i === trail.length - 1
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground'
                    }
                  >
                    {step}
                  </span>
                </span>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">
              A page it does not have an index entry for
            </span>
          )}
          <Raw>{route}</Raw>
        </Fact>

        <Fact label="venue">
          {venueName ?? <span className="text-muted-foreground">—</span>}
          {venueSerial ? <Raw>{venueSerial}</Raw> : null}
        </Fact>

        <Fact label="organisation">
          {orgName ?? <span className="text-muted-foreground">—</span>}
          {orgId ? <Raw>{orgId}</Raw> : null}
        </Fact>

        {movedTo ? <Fact label="it just moved you to">{movedTo}</Fact> : null}

        <Fact label="microphone">
          {muted ? 'off' : voice === 'listening' ? 'listening' : voice}
        </Fact>

        <Fact label="conversation">
          {caseId ? (
            <Raw inline>{caseId}</Raw>
          ) : (
            <span className="text-muted-foreground">not open yet</span>
          )}
        </Fact>
      </div>

      <p className="pt-3 text-[11px] leading-relaxed text-muted-foreground/70">
        It cannot see what is on this page, only which page it is. It cannot
        see other tabs, other conversations, or anything outside this
        organisation.
      </p>
    </PanelFrame>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 py-2">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
        {label}
      </span>
      <span className="min-w-0 break-words text-sm text-foreground">
        {children}
      </span>
    </div>
  )
}

/**
 * The id or the literal route, kept but demoted.
 *
 * It is the thing you paste into a bug report and the thing nobody reads
 * otherwise, so it is smaller, quieter and selectable.
 */
function Raw({ children, inline }: { children: ReactNode; inline?: boolean }) {
  return (
    <code
      className={`${inline ? '' : 'mt-0.5 block '}select-all break-all font-mono text-[10px] leading-relaxed text-muted-foreground/60`}
    >
      {children}
    </code>
  )
}
